import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiClock, FiCheckCircle, FiAlertCircle, FiAward, FiFlag } from 'react-icons/fi';

const QuizPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { questions = [], config } = location.state || {};
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(config?.numQuestions * 60 || 600);
  
  const marks = config?.marks || 1;
  const negativeMarks = config?.negativeMarks || 0;

  useEffect(() => {
    if (!questions.length) {
      navigate('/quiz-selection');
      return;
    }

    // Timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const currentQ = questions[currentQuestion];
  const options = config?.useAI 
    ? currentQ?.options || []
    : [
        { text: currentQ?.opt_a, label: 'A' },
        { text: currentQ?.opt_b, label: 'B' },
        { text: currentQ?.opt_c, label: 'C' },
        { text: currentQ?.opt_d, label: 'D' },
      ].filter(opt => opt.text);

  const handleAnswerSelect = (answer) => {
    setAnswers({
      ...answers,
      [currentQuestion]: {
        question_id: currentQ.id,
        selected: answer,
      },
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowConfirm(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleMarkForReview = () => {
    setMarkedForReview({
      ...markedForReview,
      [currentQuestion]: !markedForReview[currentQuestion]
    });
  };

  const handleSubmit = async () => {
    try {
      const answerArray = Object.values(answers);
      
      // Prepare submission data
      const submissionData = {
        userId: user.id,
        answers: answerArray,
        config: config
      };
      
      const response = await quizAPI.submitQuiz(submissionData);
      
      navigate('/results', { 
        state: { 
          results: response.data,
          questions,
          answers,
          config
        } 
      });
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert('Failed to submit quiz. Please try again.');
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const answeredCount = Object.keys(answers).length;
  const reviewedCount = Object.keys(markedForReview).filter(key => markedForReview[key]).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-x-hidden relative">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md shadow-xl border-b border-primary-100/50">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Timer - Enhanced */}
            <div className={`flex items-center space-x-2 sm:space-x-3 px-4 sm:px-5 py-3 sm:py-3.5 rounded-2xl shadow-lg border-2 ${
              timeLeft < 60 
                ? 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-200 animate-pulse' 
                : timeLeft < 300
                ? 'bg-gradient-to-r from-amber-50 to-yellow-100 text-amber-700 border-amber-200'
                : 'bg-gradient-to-r from-emerald-50 to-green-100 text-emerald-700 border-emerald-200'
            }`}>
              <div className="relative">
                <div className={`p-2 rounded-xl ${
                  timeLeft < 60 ? 'bg-red-200/50' : timeLeft < 300 ? 'bg-amber-200/50' : 'bg-emerald-200/50'
                }`}>
                  <FiClock className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                {timeLeft < 60 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold opacity-75 hidden sm:block uppercase tracking-wide">Time Left</div>
                <span className="text-xl sm:text-2xl font-black tabular-nums tracking-tight">
                  {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                </span>
              </div>
            </div>

            {/* Progress Bar - Enhanced */}
            <div className="flex-1 w-full sm:max-w-md bg-white/80 backdrop-blur-sm rounded-2xl p-3 sm:p-4 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-3 text-xs sm:text-sm">
                <span className="font-bold text-gray-800 flex items-center bg-blue-50 px-2 py-1 rounded-lg">
                  <FiAward className="mr-1.5 w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  Q {currentQuestion + 1}/{questions.length}
                </span>
                <span className="font-bold text-green-700 flex items-center bg-green-50 px-2 py-1 rounded-lg">
                  <FiCheckCircle className="mr-1.5 w-3 h-3 sm:w-4 sm:h-4" />
                  {answeredCount}/{questions.length}
                </span>
              </div>
              <div className="relative w-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-full h-4 overflow-hidden shadow-inner border border-gray-200">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 transition-all duration-700 ease-out rounded-full"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"></div>
                  <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center text-xs font-black text-gray-700 drop-shadow-sm">
                  {Math.round(progress)}%
                </div>
              </div>
            </div>

            {/* Finish Button - Always Visible */}
            <button
              onClick={() => setShowConfirm(true)}
              className="group relative flex items-center justify-center space-x-2 px-5 sm:px-7 py-3 sm:py-3.5 bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 text-white rounded-2xl font-black shadow-xl hover:shadow-2xl active:scale-95 sm:hover:scale-105 transition-all duration-300 whitespace-nowrap border-2 border-white/20 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              <FiFlag className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />
              <span className="relative z-10 tracking-wide">Finish</span>
            </button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-20 sm:pb-24 relative z-10">

        {/* Two Column Layout for Desktop - Single Column for Mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
          
          {/* LEFT COLUMN: Question Card */}
          <div className="lg:col-span-2">
            {/* Question Card - Enhanced Design */}
            <div className="bg-gradient-to-br from-white via-white to-blue-50/30 rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-7 md:p-10 border-2 border-indigo-100/50 hover:border-indigo-200/50 hover:shadow-3xl transition-all duration-300 backdrop-blur-sm lg:sticky lg:top-24">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
              <span className="inline-flex items-center px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl text-xs sm:text-sm font-black shadow-lg border-2 border-white/20">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 flex items-center justify-center mr-2 sm:mr-2.5">
                  <FiAlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                Question {currentQuestion + 1} / {questions.length}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {markedForReview[currentQuestion] && (
                  <span className="text-xs sm:text-sm text-purple-700 font-bold flex items-center bg-gradient-to-r from-purple-100 to-purple-50 px-3 py-1.5 rounded-xl shadow-md border border-purple-200">
                    <FiFlag className="mr-1.5 w-3 h-3 sm:w-4 sm:h-4" />
                    Marked for Review
                  </span>
                )}
                {!answers[currentQuestion] && (
                  <span className="text-xs sm:text-sm text-amber-700 font-bold flex items-center bg-gradient-to-r from-amber-100 to-amber-50 px-3 py-1.5 rounded-xl shadow-md border border-amber-200 animate-pulse">
                    <FiAlertCircle className="mr-1.5 w-3 h-3 sm:w-4 sm:h-4" />
                    Not Answered Yet
                  </span>
                )}
              </div>
            </div>
            {/* Marks Information */}
            <div className="flex flex-wrap items-center gap-3 mb-5 text-xs sm:text-sm">
              <span className="flex items-center text-emerald-700 font-black bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-2 rounded-xl shadow-md border-2 border-emerald-200">
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-500 flex items-center justify-center mr-2">
                  <FiCheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                </div>
                Marks: +{marks}
              </span>
              {negativeMarks > 0 && (
                <span className="flex items-center text-red-700 font-black bg-gradient-to-r from-red-50 to-rose-50 px-4 py-2 rounded-xl shadow-md border-2 border-red-200">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-red-500 flex items-center justify-center mr-2">
                    <FiAlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                  </div>
                  Negative: -{negativeMarks}
                </span>
              )}
            </div>
            <div className="bg-gradient-to-r from-indigo-50/50 to-purple-50/50 rounded-2xl p-4 sm:p-6 border-l-4 border-indigo-500">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-relaxed break-words">
                {currentQ?.question}
              </h2>
            </div>
          </div>

          {/* Options - Enhanced Design */}
          <div className="space-y-3 sm:space-y-4">
            {options.map((option, index) => {
              const optionLabel = config?.useAI 
                ? String.fromCharCode(65 + index)
                : option.label;
              const optionText = config?.useAI ? option.text : option.text;
              const isSelected = answers[currentQuestion]?.selected === optionLabel;

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(optionLabel)}
                  className={`group relative w-full text-left p-4 sm:p-5 md:p-6 rounded-2xl border-2 transition-all duration-300 transform active:scale-95 sm:hover:scale-[1.01] overflow-hidden ${
                    isSelected
                      ? 'border-indigo-400 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 shadow-2xl'
                      : 'border-gray-200 hover:border-indigo-300 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-indigo-50/30 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 to-purple-500/5"></div>
                  )}
                  <div className="flex items-center space-x-3 sm:space-x-4 relative z-10">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center font-black text-xl sm:text-2xl flex-shrink-0 transition-all duration-300 shadow-lg ${
                      isSelected
                        ? 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white scale-110'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 group-hover:from-indigo-100 group-hover:to-purple-100 group-hover:text-indigo-700'
                    }`}>
                      {optionLabel}
                    </div>
                    <span className={`text-sm sm:text-base md:text-lg font-semibold flex-1 transition-colors break-words leading-relaxed ${
                      isSelected ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                    }`}>
                      {optionText}
                    </span>
                    {isSelected && (
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg animate-bounce">
                          <FiCheckCircle className="text-white w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

            {/* Navigation Buttons - Below Question Card */}
            <div className="space-y-3 sm:space-y-4 mt-5 sm:mt-6">
          {/* Mark for Review Button */}
          <button
            onClick={handleMarkForReview}
            className={`group relative w-full px-5 sm:px-7 py-4 sm:py-5 rounded-2xl font-black shadow-xl hover:shadow-2xl active:scale-95 sm:hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base flex items-center justify-center overflow-hidden ${
              markedForReview[currentQuestion]
                ? 'bg-gradient-to-r from-purple-500 via-purple-600 to-pink-500 text-white border-2 border-purple-300'
                : 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-2 border-purple-300 hover:from-purple-100 hover:to-pink-100'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            <FiFlag className="mr-2.5 w-5 h-5 sm:w-6 sm:h-6 relative z-10" />
            <span className="relative z-10">{markedForReview[currentQuestion] ? 'Unmark Review' : 'Mark for Review'}</span>
          </button>
          
          {/* Previous/Next Buttons */}
          <div className="flex gap-3 sm:gap-4">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="group relative flex-1 px-5 sm:px-7 py-4 sm:py-5 bg-white text-gray-800 rounded-2xl font-black shadow-xl hover:shadow-2xl disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 sm:hover:scale-[1.02] transition-all duration-300 border-2 border-gray-200 hover:border-indigo-300 text-sm sm:text-base overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/0 to-indigo-50/0 group-hover:from-indigo-50 group-hover:to-purple-50 transition-all duration-300"></div>
              <span className="relative z-10">← Previous</span>
            </button>
            <button
              onClick={handleNext}
              disabled={!answers[currentQuestion]}
              className="group relative flex-1 px-5 sm:px-7 py-4 sm:py-5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-2xl font-black shadow-xl hover:shadow-2xl disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 sm:hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base border-2 border-white/20 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              <span className="relative z-10">{currentQuestion === questions.length - 1 ? 'Last Question ✓' : 'Next →'}</span>
            </button>
          </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Question Navigator Only */}
          <div className="lg:col-span-1">
            {/* Question Navigator - Enhanced with Scrollable */}
            <div className="bg-gradient-to-br from-white via-white to-indigo-50/30 rounded-2xl sm:rounded-3xl lg:rounded-xl shadow-2xl p-4 sm:p-7 lg:p-3 border-2 border-indigo-100/50 backdrop-blur-sm lg:sticky lg:top-24 lg:max-h-[calc(100vh-120px)]">
          <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-start justify-between gap-3 lg:gap-1.5 mb-4 lg:mb-2">
            <h3 className="text-base lg:text-sm font-black text-gray-900 flex items-center">
              <div className="w-8 h-8 lg:w-7 lg:h-7 rounded-xl lg:rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mr-2 lg:mr-1.5 shadow-lg">
                <FiAward className="text-white w-4 h-4 lg:w-4 lg:h-4" />
              </div>
              Navigator
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-bold">
              <span className="flex items-center whitespace-nowrap bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 mr-1 shadow-sm"></span>
                Current
              </span>
              <span className="flex items-center whitespace-nowrap bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 mr-1 shadow-sm"></span>
                Answered
              </span>
              <span className="flex items-center whitespace-nowrap bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">
                <span className="w-2 h-2 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 mr-1 shadow-sm"></span>
                Review
              </span>
              <span className="flex items-center whitespace-nowrap bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                <span className="w-2 h-2 rounded-full bg-gray-300 mr-1"></span>
                Pending
              </span>
            </div>
          </div>
          <div className="overflow-y-auto lg:max-h-[calc(100vh-200px)] pr-1">
          <div className="m-2 grid grid-cols-5 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {questions.map((_, index) => {
              const isMarked = markedForReview[index];
              const isAnswered = answers[index];
              const isCurrent = index === currentQuestion;
              
              let bgClass = 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 hover:from-gray-200 hover:to-gray-300 hover:text-gray-800 border-2 border-gray-300';
              if (isCurrent) {
                bgClass = 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white ring-4 ring-indigo-200 scale-110 shadow-2xl border-2 border-white';
              } else if (isMarked) {
                bgClass = 'bg-gradient-to-br from-purple-400 via-purple-500 to-pink-500 text-white hover:shadow-2xl shadow-lg border-2 border-purple-300';
              } else if (isAnswered) {
                bgClass = 'bg-gradient-to-br from-emerald-400 to-green-500 text-white hover:shadow-2xl shadow-lg border-2 border-emerald-300';
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`aspect-square rounded-xl lg:rounded-lg font-black text-xs sm:text-sm lg:text-xs transition-all duration-300 transform active:scale-90 sm:hover:scale-110 flex items-center justify-center ${bgClass}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
          </div>
        </div>
          </div>

        </div>
      </div>

      {/* Confirm Modal - Enhanced */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gradient-to-br from-black/70 via-indigo-900/40 to-purple-900/40 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-white via-white to-indigo-50/30 rounded-2xl sm:rounded-3xl shadow-3xl max-w-lg w-full overflow-hidden transform transition-all animate-scaleIn max-h-[90vh] overflow-y-auto border-2 border-indigo-100">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 p-5 sm:p-7 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="flex items-center justify-center mb-3 relative z-10">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border-2 border-white/30">
                  <FiFlag className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-center tracking-tight relative z-10">Submit Exam?</h3>
              <p className="text-center text-white/80 text-sm mt-2 relative z-10">Review your answers before final submission</p>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-7">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-7">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-3 sm:p-4 text-center border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 mx-auto mb-2 flex items-center justify-center shadow-md">
                    <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-emerald-700">{answeredCount}</div>
                  <div className="text-xs sm:text-sm text-emerald-600 font-bold mt-1">Answered</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-3 sm:p-4 text-center border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 mx-auto mb-2 flex items-center justify-center shadow-md">
                    <FiFlag className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-purple-700">{reviewedCount}</div>
                  <div className="text-xs sm:text-sm text-purple-600 font-bold mt-1">Review</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-3 sm:p-4 text-center border-2 border-amber-200 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-2 flex items-center justify-center shadow-md">
                    <FiAlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-2xl sm:text-3xl font-black text-amber-700">{questions.length - answeredCount}</div>
                  <div className="text-xs sm:text-sm text-amber-600 font-bold mt-1">Pending</div>
                </div>
              </div>

              {/* Warning */}
              {answeredCount < questions.length ? (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl p-4 sm:p-5 mb-5 sm:mb-7 shadow-lg">
                  <div className="flex items-start">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 shadow-md">
                      <FiAlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-black text-amber-900 mb-1.5 text-base sm:text-lg">⚠️ Warning!</p>
                      <p className="text-sm sm:text-base text-amber-700 font-medium leading-relaxed">
                        You have <span className="font-black text-amber-900">{questions.length - answeredCount}</span> unanswered question(s). 
                        Unanswered questions will be marked as incorrect.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-2xl p-4 sm:p-5 mb-5 sm:mb-7 shadow-lg">
                  <div className="flex items-center justify-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center mr-3 shadow-md">
                      <FiCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <p className="font-black text-emerald-800 text-base sm:text-lg">✅ All questions answered!</p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 sm:gap-4">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="group relative flex-1 px-5 sm:px-7 py-4 sm:py-5 bg-white text-gray-800 rounded-2xl font-black hover:bg-gray-50 active:scale-95 transition-all duration-300 border-2 border-gray-300 hover:border-gray-400 text-sm sm:text-base shadow-lg hover:shadow-xl overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-50/0 to-gray-50/0 group-hover:from-gray-100 group-hover:to-gray-50 transition-all duration-300"></div>
                  <span className="relative z-10">← Go Back</span>
                </button>
                <button
                  onClick={handleSubmit}
                  className="group relative flex-1 px-5 sm:px-7 py-4 sm:py-5 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 text-white rounded-2xl font-black shadow-xl hover:shadow-2xl active:scale-95 sm:hover:scale-105 transition-all duration-300 text-sm sm:text-base border-2 border-white/20 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                  <span className="relative z-10">Submit Now ✓</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizPage;

